import React from "react";

import { matchPath, useNavigate } from "react-router-dom";

import { availablePaths } from "../../App";
import useAuth from "./authContext";
import useFirebase from "./firebaseContext";

import { AuthService } from "../services/AuthService";
import { Dev } from "../util/consoleUtil";
import useModal from "./modalContext";

import SideMenuDrawer from "../../components/SideMenuDrawer";

import { OnlyChildren } from "../../types/common";

type MainStateContextType = {
  isStandalone: boolean;
  showSideMenuDrawer: boolean;
  setShowSideMenuDrawer: React.Dispatch<React.SetStateAction<boolean>>;
  getOperatingSystem: () => AvailableOperatingType;
  userEnableToInteract: (requirement?: "login" | "verified" | "enabled") => boolean;
  customAwaiter: (timeMs: number) => Promise<void>;
  deviceInfo: { [key: string]: any };
  openLink: (link: string, type?: "in-app" | "external" | "location") => void;
};

type AvailableOperatingType = "Windows" | "Android" | "iOS" | "Unknow";

const MainStateContext = React.createContext<MainStateContextType | null>(null);

export function MainStateProvider({ children }: OnlyChildren) {
  const navigate = useNavigate();

  const { functions } = useFirebase();
  const { pushAlert } = useModal();

  const UserUpdate = AuthService.UpdateOrCreate();

  const { currentUserState, currentUserInfo } = useAuth();

  const [isStandalone] = React.useState<boolean>(
    window.matchMedia("(display-mode: standalone)").matches
  );

  const [deviceInfo, setDeviceInfo] = React.useState<{ [key: string]: any } | null>();

  const [showSideMenuDrawer, setShowSideMenuDrawer] = React.useState(false);

  const getDeviceInfo = () => {
    try {
      const currentDeviceInfo = {
        mobile: (navigator as any).userAgentData?.mobile || false,
        os: getOperatingSystem() || false,
        platform: navigator.platform || (navigator as any).userAgentData?.platform || false,
        vendor: navigator.vendor || false,
        standalone:
          (navigator as any).standalone ??
          window.matchMedia("(display-mode: standalone)").matches ??
          false,
      };

      setDeviceInfo(currentDeviceInfo);
    } catch (error) {
      console.log(...new Dev("getDeviceInfo", error).Error());
    }
  };

  const userEnableToInteract = (requirement: "login" | "verified" | "enabled" = "enabled") => {
    if (!currentUserState) {
      pushAlert({
        type: "error",
        message: "Para acessar essa funcionalidade você precisa fazer login.",
      });
      return false;
    }

    if (requirement == "login") return true;

    if (!currentUserState.emailVerified) {
      pushAlert({
        type: "error",
        message:
          "Você precisa confirmar seu email para acessar essa funcionalidade. Vá no seu perfil e envie o email de confirmação.",
      });
      return false;
    }

    if (requirement == "verified") return true;

    if (!currentUserInfo?.enabled) {
      pushAlert({
        type: "error",
        message:
          "Seu usuário não está mais habilitado a interagir no aplicativo. Caso ache essa penalidade injusta, entre em contato conosco.",
      });
      return false;
    }

    return true;
  };

  const getOperatingSystem = () => {
    const userAgent = navigator.userAgent;

    if (/Windows/i.test(userAgent)) {
      return "Windows";
    }

    if (/Android/i.test(userAgent)) {
      return "Android";
    }

    if (/iPad|iPhone|iPod|Macintosh/.test(userAgent)) {
      return "iOS";
    }

    return "Unknow";
  };

  const checkDeviceInfo = async () => {
    try {
      if (!currentUserState || !deviceInfo) return;

      if (
        typeof currentUserInfo?.device !== "object" ||
        Object.entries(currentUserInfo?.device || {}).toString() !==
          Object.entries(deviceInfo).toString()
      ) {
        await UserUpdate.runRequest(currentUserState?.uid || "", { device: deviceInfo });
      }
      await UserUpdate.runRequest(currentUserState?.uid || "", { device: deviceInfo });
    } catch (error) {
      console.log(...new Dev("checkDeviceInfo", error).Error());
    }
  };

  const customAwaiter = (timeMs: number) => {
    return new Promise((resolve) => setTimeout(resolve, timeMs));
  };

  const openLink = (link: string, type: "in-app" | "external" | "location" = "external") => {
    try {
      if (type === "location") {
        if (availablePaths.some((availablePath) => matchPath(availablePath, link))) {
          navigate(link);
        } else {
          pushAlert({ type: "error", message: "A página acessada não existe" });
          console.log(...new Dev("openLink: Not available", link).Error());
        }
      } else if ("cordova" in window) {
        (window as any)?.cordova?.InAppBrowser?.open(link, "_system", "location=yes");
      } else {
        /**
         * _system, _blank => Open in new tab both browse and PWA
         * _parent, _top, _self => Open override the app both browse and PWA
         * But, on Android PWA (aparently), all open override.
         */
        window.open(link, "_system")?.focus();
      }
    } catch (error) {
      pushAlert({ type: "error", message: "Não foi possível abrir o link" });
      console.log(...new Dev("openLink", error).Error());
    }
  };

  React.useEffect(() => {
    getDeviceInfo();
  }, []);

  React.useEffect(() => {
    if (currentUserInfo && deviceInfo) checkDeviceInfo();
  }, [deviceInfo, currentUserInfo]);

  //

  const value = {
    customAwaiter,
    isStandalone,
    getOperatingSystem,
    showSideMenuDrawer,
    setShowSideMenuDrawer,
    deviceInfo,
    userEnableToInteract,
    openLink,
  };

  return (
    <MainStateContext.Provider value={value as MainStateContextType}>
      <SideMenuDrawer />
      {children}
    </MainStateContext.Provider>
  );
}

export default function useMainState() {
  return React.useContext(MainStateContext) as MainStateContextType;
}
